/**
 * @description
 * Deployment settings component for marketplace configuration.
 * Handles worker deployment defaults and configuration.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { useState } from 'react';
import { DeploymentSettingsProps } from './types';
import { useMarketplaceConfig } from './hooks/useMarketplaceConfig';

/**
 * Deployment settings component
 */
export function DeploymentSettings({ onRefresh }: DeploymentSettingsProps) {
  const { config, updateConfig } = useMarketplaceConfig();
  const [formData, setFormData] = useState({
    region: config?.defaultWorkerSettings?.region || 'auto',
    compatibilityDate: config?.defaultWorkerSettings?.compatibilityDate || '2024-01-01',
    compatibilityFlags: config?.defaultWorkerSettings?.compatibilityFlags?.join(',') || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const updateData = {
        defaultWorkerSettings: {
          region: formData.region,
          compatibilityDate: formData.compatibilityDate,
          compatibilityFlags: formData.compatibilityFlags.split(',').map(f => f.trim()).filter(f => f),
        },
      };
      await updateConfig(updateData);
      await onRefresh();
    } catch (error) {
      console.error('Error updating deployment settings:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Deployment Settings</h2>
        <p className="text-sm text-muted-foreground">
          Configure default settings for marketplace app deployments.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="region" className="block text-sm font-medium mb-2">
            Default Worker Region
          </label>
          <select
            id="region"
            value={formData.region}
            onChange={(e) => handleInputChange('region', e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="auto">Auto (Recommended)</option>
            <option value="us-east-1">US East</option>
            <option value="us-west-1">US West</option>
            <option value="eu-west-1">Europe</option>
            <option value="ap-southeast-1">Asia Pacific</option>
          </select>
        </div>

        <div>
          <label htmlFor="compatibilityDate" className="block text-sm font-medium mb-2">
            Compatibility Date
          </label>
          <input
            id="compatibilityDate"
            type="date"
            value={formData.compatibilityDate}
            onChange={(e) => handleInputChange('compatibilityDate', e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            This determines which Cloudflare Workers features are available.
          </p>
        </div>

        <div>
          <label htmlFor="compatibilityFlags" className="block text-sm font-medium mb-2">
            Compatibility Flags
          </label>
          <input
            id="compatibilityFlags"
            type="text"
            value={formData.compatibilityFlags}
            onChange={(e) => handleInputChange('compatibilityFlags', e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            placeholder="nodejs_compat, streams"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Comma-separated list of compatibility flags (e.g., nodejs_compat, streams).
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            {isSubmitting ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}